import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';

// Renderiza os documentos de ajuda (src/docs/*.md) com a identidade do app. Links internos
// ("/media/taxonomy") navegam pelo router; externos abrem em nova aba.

const components: Components = {
  h1: ({ children }) => <h1 className="text-h3 mt-2 mb-3">{children}</h1>,
  h2: ({ children }) => <h2 className="text-h4 mt-6 mb-2 pb-1 border-b border-slate-200">{children}</h2>,
  h3: ({ children }) => <h3 className="font-semibold text-slate-800 mt-4 mb-1.5">{children}</h3>,
  p: ({ children }) => <p className="text-slate-700 leading-relaxed my-2">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-1 my-2 marker:text-slate-400 text-slate-700">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-1 my-2 marker:text-slate-400 text-slate-700">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
  code: ({ children }) => <code className="px-1 py-0.5 rounded bg-slate-100 text-[0.85em] font-mono text-slate-800 [overflow-wrap:anywhere]">{children}</code>,
  blockquote: ({ children }) => (
    <blockquote className="border-l-4 border-emerald-400 bg-emerald-50/60 rounded-r-lg px-3 py-1 my-3 text-slate-700">{children}</blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-3 border border-slate-200 rounded-lg">
      <table className="w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
  th: ({ children }) => <th className="text-left font-semibold text-slate-700 px-3 py-2 border-b border-slate-200">{children}</th>,
  td: ({ children }) => <td className="px-3 py-2 border-b border-slate-100 align-top text-slate-700">{children}</td>,
  hr: () => <hr className="my-4 border-slate-200" />,
  a: ({ href, children }) =>
    href && href.startsWith('/') ? (
      <Link to={href} className="text-emerald-700 hover:underline">
        {children}
      </Link>
    ) : (
      <a href={href} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">
        {children}
      </a>
    ),
};

export const MarkdownView: React.FC<{ source: string }> = ({ source }) => (
  <div className="text-body min-w-0 [overflow-wrap:anywhere]">
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {source}
    </ReactMarkdown>
  </div>
);
